import { useEffect, useState, useMemo, useRef } from "react";
import queryString from "query-string";
import { Link } from "react-router-dom";
import sweetAlert from "sweetalert";
import moment from "moment";

import { Breadcrumbs, Loading, Page, Pagination, Panel } from "components";
import vouchersAPI from "services/vouchers";
import { Access, PermissionsEnum } from "common";

import { Filters } from "./components";

const breadcrumbs = [
  { name: "Home", to: "/" },
  { name: "Campanhas de Cashback Externo", to: "" },
];

const initialState = {
  title: "",
  start_at: null,
  end_at: null,
};

export function List(props) {
  const [isLoading, setIsLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [filters, setFilters] = useState(initialState);

  const debounceTimer = useRef(null);

  const pagination = useMemo(() => {
    const params = queryString.parse(props.location.search);
    const page = (params.page && Number(params.page)) || 1;
    const per = (params.per && Number(params.per)) || 25;

    return { page, per };
  }, [props.location.search]);

  function handleFiltersChange(name, value) {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  }

  function handleFiltersClean(event) {
    event.preventDefault();
    setFilters(initialState);
  }

  useEffect(() => {
    function fetchData() {
      setIsLoading(true);

      const { page, per } = pagination;

      const options = {
        params: {
          per,
          page,
          title: filters.title ? filters.title : null,
          start_at: filters.start_at ? filters.start_at : null,
          end_at: filters.end_at ? filters.end_at : null,
        },
      };

      vouchersAPI
        .get("external/campaigns", options)
        .then(({ data }) => {
          if (!data || !data.data) {
            return;
          }

          if (Array.isArray(data.data)) {
            setCampaigns(
              data.data.map(({ id, attributes }) => ({
                id,
                title: attributes.title,
                cashback: attributes.cashback,
                active: attributes.active,
                start_at: attributes.start_at,
                end_at: attributes.end_at,
              }))
            );
          }

          if (data.meta && data.meta.total_count !== undefined) {
            setTotalCampaigns(data.meta.total_count);
          }
        })
        .catch((error) => {
          sweetAlert(
            "Algo deu errado!",
            "Não foi possível buscar os dados. Por favor, recarregue a página ou tente fazer o login novamente.",
            "error"
          );
          console.error(error);
        })
        .finally(() => setIsLoading(false));
    }

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(fetchData, 500);
  }, [pagination, filters]);

  return (
    <Page>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <Page.Title>Campanhas de Cashback Externo</Page.Title>

      <Filters
        filters={filters}
        onChange={handleFiltersChange}
        onClean={handleFiltersClean}
      />

      <Panel className="panel-list">
        <div className="vouchers-options">
          <p>
            Exibindo {campaigns.length} de {totalCampaigns} campanhas
          </p>
          {Access.hasPermissions(
            PermissionsEnum.EDIT_EXTERNAL_CASHBACK_CAMPAIGN
          ) && (
            <Link
              className="button-add margin-bottom-20"
              to="/external-cashback-campaigns/create"
            >
              Adicionar campanha
            </Link>
          )}
        </div>

        <>
          <table className="table table-curved">
            <thead>
              <tr>
                <th>Título</th>
                <th>Cashback</th>
                <th>Início em</th>
                <th>Fim em</th>
                <th>Ativo</th>
                <th className="text-center">Ação</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <Loading type="page" size="large" />
              ) : !campaigns.length ? (
                <tr>
                  <td className="text-center" colSpan="10">
                    Nenhuma campanha encontrada.
                  </td>
                </tr>
              ) : (
                campaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td>{campaign.title}</td>
                    <td>{`R$ ${campaign.cashback}`}</td>
                    <td>
                      {campaign.start_at
                        ? moment(campaign.start_at).format("DD-MM-YYYY HH:mm")
                        : ""}
                    </td>
                    <td>
                      {campaign.end_at
                        ? moment(campaign.end_at).format("DD-MM-YYYY HH:mm")
                        : ""}
                    </td>
                    <td
                      className={
                        campaign.active ? "label-active" : "label-inactive"
                      }
                    >
                      {campaign.active ? "Sim" : "Não"}
                    </td>
                    <td className="text-right">
                      {Access.hasPermissions(
                        PermissionsEnum.EDIT_EXTERNAL_CASHBACK_CAMPAIGN
                      ) && (
                        <Link
                          to={`/external-cashback-campaigns/${campaign.id}/edit`}
                          className="button-icon"
                        >
                          <span className="glyph glyph-pencil" />
                        </Link>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            <tfoot>
              <tr>
                <th colSpan="6" />
              </tr>
            </tfoot>
          </table>

          <Pagination
            current={pagination.page}
            per={pagination.per}
            total={totalCampaigns}
            {...props}
          />

          {Access.hasPermissions(
            PermissionsEnum.EDIT_EXTERNAL_CASHBACK_CAMPAIGN
          ) && (
            <Link
              className="button-add"
              to="/external-cashback-campaigns/create"
            >
              Adicionar campanha
            </Link>
          )}
        </>
      </Panel>
    </Page>
  );
}
